import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { schoolLogoAPI } from '../services/api';

const normalizeApiBase = (value) => {
  const trimmed = (value || '/api').replace(/\/+$/, '');
  return trimmed.endsWith('/api') ? trimmed : `${trimmed}/api`;
};

const API = normalizeApiBase(process.env.REACT_APP_API_URL);

const fmtCurrency = (n) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);

// Convert school name to URL-friendly slug (must match backend toSlug)
function toSlug(name) {
  return (name || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

function parseParentRoute() {
  const parts = window.location.pathname.split('/');
  const idx = parts.indexOf('parent');
  return {
    schoolCode: idx >= 0 ? decodeURIComponent(parts[idx + 1] || '') : '',
    studentId: idx >= 0 ? parts[idx + 2] : '',
  };
}

function Logo({ src }) {
  const resolved = schoolLogoAPI.resolveUrl(src || '');
  if (!resolved) return null;
  return (
    <img
      src={resolved}
      alt="School Logo"
      style={{ width: 64, height: 64, objectFit: 'contain', borderRadius: 12, background: '#fff', padding: 6 }}
      onError={(e) => { e.currentTarget.style.display = 'none'; }}
    />
  );
}

// ── Parent-facing chat messages tab ────────────────────────────────────────
// Shows school-wide / class / individual messages addressed to this parent.
// Mobile is read from sessionStorage (set during the QR-code lookup step).
// If it isn't there (e.g. parent opened the link directly), we prompt for it.
function ParentMessagesTab({ schoolCode, studentId }) {
  const [mobile, setMobile] = useState(() => {
    try { return sessionStorage.getItem('gs_parent_mobile') || ''; } catch { return ''; }
  });
  const [mobileInput, setMobileInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Helpful labels for the system-generated thread keys.
  const labelFor = (key, title) => {
    if (title) return title;
    if (key === 'bc:students') return 'All Students';
    if (key === 'bc:parents')  return 'All Parents';
    if (key.startsWith('cls:') && key.endsWith(':parents')) return `Class ${key.slice(4, -8)} Parents`;
    if (key.startsWith('cls:')) return `Class ${key.slice(4)}`;
    if (key.startsWith('stu:') && key.endsWith(':parent')) return 'For your child';
    if (key.startsWith('stu:')) return 'For your child';
    return key;
  };

  const fetchMessages = useCallback(() => {
    if (!mobile) return;
    setLoading(true);
    setError('');
    axios.get(`${API}/progress-card/${encodeURIComponent(schoolCode)}/${studentId}/messages?mobile=${encodeURIComponent(mobile)}`)
      .then((res) => {
        if (res.data.success) setMessages(res.data.data || []);
        else setError(res.data.message || 'Failed to load messages');
      })
      .catch((err) => setError(err.response?.data?.message || 'Failed to load messages'))
      .finally(() => setLoading(false));
  }, [schoolCode, studentId, mobile]);

  useEffect(() => {
    fetchMessages();
    if (!mobile) return undefined;
    // Soft polling so parents see new notices without refreshing.
    const id = setInterval(fetchMessages, 15000);
    return () => clearInterval(id);
  }, [fetchMessages, mobile]);

  if (!mobile) {
    return (
      <div style={{ padding: 24, background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', maxWidth: 420, margin: '0 auto' }}>
        <h3 style={{ margin: '0 0 8px', color: '#0f172a' }}>Verify your mobile</h3>
        <p style={{ color: '#64748b', marginTop: 0, fontSize: 14 }}>
          Enter the mobile number registered with the school to view messages.
        </p>
        <input
          type="tel"
          inputMode="numeric"
          maxLength={10}
          placeholder="10-digit mobile number"
          value={mobileInput}
          onChange={(e) => setMobileInput(e.target.value.replace(/\D/g, ''))}
          style={{ width: '100%', padding: '12px 14px', border: '1px solid #cbd5e1', borderRadius: 10, fontSize: 16, boxSizing: 'border-box' }}
        />
        <button
          onClick={() => {
            const clean = mobileInput.replace(/\D/g, '').slice(-10);
            if (clean.length !== 10) { setError('Enter a valid 10-digit number'); return; }
            try { sessionStorage.setItem('gs_parent_mobile', clean); } catch { /* ignore */ }
            setMobile(clean);
          }}
          style={{ marginTop: 12, width: '100%', padding: '12px 14px', background: '#0ea5e9', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 15, cursor: 'pointer' }}
        >
          View messages
        </button>
        {error && <div style={{ marginTop: 10, color: '#b91c1c', fontSize: 13 }}>{error}</div>}
      </div>
    );
  }

  if (loading && messages.length === 0) {
    return <div style={{ padding: 24, textAlign: 'center', color: '#64748b' }}>Loading messages…</div>;
  }

  if (error) {
    return (
      <div style={{ padding: 16, borderRadius: 10, background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' }}>
        {error}
        <button
          onClick={() => { try { sessionStorage.removeItem('gs_parent_mobile'); } catch { /* ignore */ } setMobile(''); }}
          style={{ marginLeft: 12, padding: '6px 10px', borderRadius: 8, border: '1px solid #fca5a5', background: '#fff', color: '#b91c1c', cursor: 'pointer' }}
        >
          Re-enter mobile
        </button>
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div style={{ padding: 32, textAlign: 'center', background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0' }}>
        <div style={{ fontSize: 40 }}>💬</div>
        <h3 style={{ margin: '12px 0 4px', color: '#0f172a' }}>No messages yet</h3>
        <p style={{ color: '#64748b', margin: 0 }}>Notices from your school will appear here.</p>
      </div>
    );
  }

  // Group by date for a chat-like feel.
  const groups = [];
  let lastDate = '';
  messages.forEach((m) => {
    const d = new Date(m.created_at);
    const day = d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    if (day !== lastDate) { groups.push({ day, items: [] }); lastDate = day; }
    groups[groups.length - 1].items.push(m);
  });

  return (
    <div style={{ background: '#f0f2f5', padding: 12, borderRadius: 12, border: '1px solid #e2e8f0' }}>
      {groups.map((g) => (
        <div key={g.day}>
          <div style={{ textAlign: 'center', margin: '12px 0' }}>
            <span style={{ background: '#e2e8f0', color: '#475569', padding: '4px 10px', borderRadius: 999, fontSize: 12 }}>{g.day}</span>
          </div>
          {g.items.map((m) => {
            const time = new Date(m.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
            return (
              <div key={m.message_id} style={{ background: '#fff', padding: '10px 12px', borderRadius: 10, margin: '6px 0', boxShadow: '0 1px 1px rgba(0,0,0,0.04)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#0ea5e9' }}>
                    {labelFor(m.thread_key, m.thread_title)}
                  </div>
                  <div style={{ fontSize: 11, color: '#94a3b8' }}>{time}</div>
                </div>
                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 2 }}>
                  From: {m.sender_name || m.sender_username}
                </div>
                <div style={{ fontSize: 15, color: '#0f172a', whiteSpace: 'pre-wrap' }}>{m.content}</div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function ParentLookup({ schoolCode }) {
  const [mobile, setMobile] = useState('');
  const [loading, setLoading] = useState(false);
  const [metaLoading, setMetaLoading] = useState(true);
  const [error, setError] = useState('');
  const [results, setResults] = useState([]);
  const [schoolMeta, setSchoolMeta] = useState({
    school_name: '',
    school_location: '',
    school_tagline: '',
    school_logo_url: '',
  });
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showIosHelp, setShowIosHelp] = useState(false);

  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/progress-card/${encodeURIComponent(schoolCode)}/meta`)
      .then((res) => {
        if (mounted && res.data?.success && res.data?.data) {
          setSchoolMeta(res.data.data);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (mounted) setMetaLoading(false);
      });

    const isIos = /iphone|ipad|ipod/i.test(window.navigator.userAgent);
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    if (isIos && !isStandalone) setShowIosHelp(true);

    const handleBeforeInstall = (event) => {
      event.preventDefault();
      setInstallPrompt(event);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    return () => {
      mounted = false;
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, [schoolCode]);

  const handleInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    try { await installPrompt.userChoice; } catch { /* ignore */ }
    setInstallPrompt(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const clean = mobile.replace(/\D/g, '');
    if (clean.length < 10) {
      setError('Please enter a valid 10-digit mobile number');
      return;
    }
    setLoading(true);
    setError('');
    setResults([]);
    try {
      const res = await axios.get(`${API}/progress-card/${encodeURIComponent(schoolCode)}/lookup?mobile=${clean}`);
      if (!res.data.success) {
        setError(res.data.message || 'No students found');
        return;
      }
      if (res.data.meta) setSchoolMeta((prev) => ({ ...prev, ...res.data.meta }));
      setResults(res.data.students || []);
      // Remember the verified mobile so the inner dashboard can authorize
      // follow-up calls (e.g. fetching school messages for this parent).
      try { sessionStorage.setItem('gs_parent_mobile', clean); } catch { /* ignore */ }
      if ((res.data.students || []).length === 1) {
        window.location.href = `/parent/${encodeURIComponent(schoolCode)}/${res.data.students[0].student_id}`;
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to search. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.lookupShell}>
        <div style={styles.lookupCard}>
          <div style={{ textAlign: 'center', marginBottom: 22 }}>
            <div style={styles.lookupBadge}>Parent Dashboard</div>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
              <Logo src={schoolMeta.school_logo_url} />
            </div>
            <h1 style={styles.lookupTitle}>
              {metaLoading ? 'Loading school...' : (schoolMeta.school_name || 'School Parent App')}
            </h1>
            {schoolMeta.school_tagline ? <div style={styles.lookupSubtle}>{schoolMeta.school_tagline}</div> : null}
            {schoolMeta.school_location ? <div style={styles.lookupSubtle}>{schoolMeta.school_location}</div> : null}
            <p style={styles.lookupText}>
              Enter your mobile number to view attendance, homework, exam results, and fee updates.
            </p>
          </div>

          {(installPrompt || showIosHelp) && (
            <div style={styles.installPanel}>
              <div style={{ fontWeight: 800, color: '#0f172a', marginBottom: 4 }}>Install Parent App</div>
              <div style={{ color: '#475569', fontSize: 13, lineHeight: 1.5 }}>
                {installPrompt
                  ? 'Tap install to add this school app to your phone home screen.'
                  : 'On iPhone, tap Share in Safari and choose "Add to Home Screen".'}
              </div>
              {installPrompt ? (
                <button type="button" onClick={handleInstall} style={styles.installButton}>Install App</button>
              ) : null}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <input
              type="tel"
              value={mobile}
              maxLength={10}
              placeholder="Enter mobile number"
              onChange={(e) => setMobile(e.target.value.replace(/\D/g, ''))}
              style={styles.lookupInput}
              autoFocus
            />
            <button type="submit" disabled={loading || mobile.length < 10} style={styles.lookupButton}>
              {loading ? 'Searching...' : 'Open Dashboard'}
            </button>
          </form>

          {error && <div style={styles.errorBox}>{error}</div>}

          {results.length > 1 && (
            <div style={{ marginTop: 20 }}>
              <div style={styles.sectionMiniTitle}>Select Student</div>
              {results.map((student) => (
                <a key={student.student_id} href={`/parent/${encodeURIComponent(schoolCode)}/${student.student_id}`} style={styles.studentPick}>
                  <div style={{ fontWeight: 700, color: '#0f172a' }}>{student.student_name}</div>
                  <div style={{ fontSize: 13, color: '#64748b' }}>
                    Class {student.class} | Father: {student.father_name}
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ label, value, tone }) {
  const tones = {
    blue: ['#eff6ff', '#1d4ed8'],
    green: ['#f0fdf4', '#15803d'],
    amber: ['#fff7ed', '#c2410c'],
    rose: ['#fff1f2', '#be123c'],
  };
  const [bg, color] = tones[tone] || tones.blue;
  return (
    <div style={{ ...styles.summaryCard, background: bg }}>
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 800, color }}>{value}</div>
    </div>
  );
}

// ── Attendance ring SVG ───────────────────────────────────────────────────────
function AttendanceRing({ pct }) {
  const r = 40;
  const circ = 2 * Math.PI * r;
  const filled = Math.min(Math.max(pct, 0), 100) / 100 * circ;
  const color = pct >= 75 ? '#16a34a' : pct >= 60 ? '#f59e0b' : '#dc2626';
  return (
    <svg width={110} height={110} viewBox="0 0 110 110" aria-label={`${pct}% attendance`}>
      <circle cx={55} cy={55} r={r} fill="none" stroke="#e2e8f0" strokeWidth={12} />
      <circle
        cx={55} cy={55} r={r} fill="none"
        stroke={color} strokeWidth={12}
        strokeDasharray={`${filled} ${circ - filled}`}
        strokeLinecap="round"
        transform="rotate(-90 55 55)"
      />
      <text x={55} y={62} textAnchor="middle" fontSize={20} fontWeight="bold" fill={color}>{pct}%</text>
    </svg>
  );
}

// ── Grade badge ───────────────────────────────────────────────────────────────
function GradeBadge({ grade }) {
  const map = {
    'A+': ['#dcfce7', '#15803d'], A: ['#dcfce7', '#15803d'],
    'B+': ['#dbeafe', '#1d4ed8'], B: ['#dbeafe', '#1d4ed8'],
    C: ['#fef9c3', '#a16207'], D: ['#ffedd5', '#c2410c'], F: ['#ffe4e6', '#be123c'],
  };
  const [bg, color] = map[grade] || ['#f1f5f9', '#64748b'];
  return (
    <span style={{ background: bg, color, fontWeight: 800, borderRadius: 6, padding: '2px 8px', fontSize: 12 }}>
      {grade || '–'}
    </span>
  );
}

// ── Parent-facing LIVE BUS tracking tab ────────────────────────────────────
// Polls the public parent endpoint every 10 s to show:
//   • current bus status (which stop, morning/evening trip)
//   • child's own boarding state (boarded / absent / alighted)
//   • timeline of the driver's taps for the active trip
// Mobile is verified against the student's registered mobile_number.
function ParentBusTab({ schoolCode, studentId }) {
  const [mobile, setMobile] = useState(() => {
    try { return sessionStorage.getItem('gs_parent_mobile') || ''; } catch { return ''; }
  });
  const [mobileInput, setMobileInput] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lastFetch, setLastFetch] = useState(null);

  const fetchLive = useCallback(() => {
    if (!mobile) return;
    axios.get(`${API}/transport-trip/parent/live/${encodeURIComponent(schoolCode)}/${studentId}?mobile=${encodeURIComponent(mobile)}`)
      .then((res) => {
        if (res.data.success) { setData(res.data.data); setError(''); }
        else setError(res.data.message || 'Failed to load bus info');
      })
      .catch((err) => setError(err.response?.data?.message || 'Failed to load bus info'))
      .finally(() => { setLoading(false); setLastFetch(new Date()); });
  }, [schoolCode, studentId, mobile]);

  useEffect(() => {
    if (!mobile) return undefined;
    setLoading(true);
    fetchLive();
    const id = setInterval(fetchLive, 10000);
    const onVis = () => { if (document.visibilityState === 'visible') fetchLive(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVis); };
  }, [fetchLive, mobile]);

  // ── Mobile-entry gate ─────────────────────────────────────────────────────
  if (!mobile) {
    return (
      <div style={{ padding: 24, background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', maxWidth: 420, margin: '0 auto' }}>
        <h3 style={{ margin: '0 0 8px', color: '#0f172a' }}>🚌 Verify your mobile</h3>
        <p style={{ color: '#64748b', marginTop: 0, fontSize: 14 }}>
          Enter the mobile number registered with the school to view live bus status.
        </p>
        <input
          type="tel"
          inputMode="numeric"
          maxLength={10}
          placeholder="10-digit mobile number"
          value={mobileInput}
          onChange={(e) => setMobileInput(e.target.value.replace(/\D/g, ''))}
          style={{ width: '100%', padding: '12px 14px', border: '1px solid #cbd5e1', borderRadius: 10, fontSize: 16, boxSizing: 'border-box' }}
        />
        <button
          onClick={() => {
            const clean = mobileInput.replace(/\D/g, '').slice(-10);
            if (clean.length !== 10) { setError('Enter a valid 10-digit number'); return; }
            try { sessionStorage.setItem('gs_parent_mobile', clean); } catch { /* ignore */ }
            setMobile(clean);
          }}
          style={{ marginTop: 12, width: '100%', padding: '12px 14px', background: '#0ea5e9', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 15, cursor: 'pointer' }}
        >
          Track bus
        </button>
        {error && <div style={{ marginTop: 10, color: '#b91c1c', fontSize: 13 }}>{error}</div>}
      </div>
    );
  }

  if (loading && !data) return <div style={{ padding: 24, textAlign: 'center', color: '#64748b' }}>Loading bus status…</div>;

  if (error && !data) {
    return (
      <div style={{ padding: 16, borderRadius: 10, background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' }}>
        {error}
        <button
          onClick={() => { try { sessionStorage.removeItem('gs_parent_mobile'); } catch { /* ignore */ } setMobile(''); }}
          style={{ marginLeft: 12, padding: '6px 10px', borderRadius: 8, border: '1px solid #fca5a5', background: '#fff', color: '#b91c1c', cursor: 'pointer' }}
        >
          Re-enter mobile
        </button>
      </div>
    );
  }

  if (!data) return null;

  // Not enrolled on any bus route
  if (!data.enrolled) {
    return (
      <div style={{ padding: 32, textAlign: 'center', background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0' }}>
        <div style={{ fontSize: 40 }}>🚌</div>
        <h3 style={{ margin: '12px 0 4px', color: '#0f172a' }}>No bus assigned</h3>
        <p style={{ color: '#64748b', margin: 0 }}>{data.message || 'Your child is not assigned to a bus route.'}</p>
      </div>
    );
  }

  const { route, child_stop, active_trip, current_stop_name, progress, boarding, events = [], trips = [] } = data;

  // Compact human-readable event labels
  const EVENT_LABELS = {
    arrived_pickup:  { icon: '🚌', text: (stop) => `Bus arrived at ${stop || 'stop'}` },
    departed_pickup: { icon: '➡️', text: (stop) => `Bus departed from ${stop || 'stop'}` },
    child_boarded:   { icon: '✅', text: () => 'Your child boarded the bus' },
    child_absent:    { icon: '❌', text: () => 'Your child was marked absent' },
    reached_school:  { icon: '🏫', text: () => 'Bus reached school' },
    started_return:  { icon: '🔄', text: () => 'Evening return started' },
    arrived_drop:    { icon: '🚌', text: (stop) => `Bus arrived at ${stop || 'stop'}` },
    child_alighted:  { icon: '🏠', text: () => 'Your child got off the bus' },
  };

  const fmtTime = (ts) => {
    if (!ts) return '';
    try { return new Date(ts).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }); } catch { return ''; }
  };

  // Big status pill
  let heroStatus = 'No trip started yet today';
  let heroSub = child_stop ? `Your child's stop: ${child_stop.stop_name}` : '';
  let heroColor = '#64748b';
  if (active_trip) {
    if (active_trip.status === 'completed') {
      heroStatus = active_trip.trip_type === 'morning' ? '✅ Morning trip completed' : '✅ Evening trip completed';
      heroColor = '#16a34a';
    } else if (current_stop_name) {
      heroStatus = `🚌 Bus at ${current_stop_name}`;
      heroColor = '#0ea5e9';
      if (progress && progress.total) heroSub = `Stop ${progress.current} of ${progress.total} • ${active_trip.trip_type === 'morning' ? 'Morning pickup' : 'Evening drop'}`;
    } else {
      heroStatus = active_trip.trip_type === 'morning' ? '🚌 Morning trip started' : '🚌 Evening trip started';
      heroColor = '#0ea5e9';
    }
  }

  // Child boarding banner
  let childBanner = null;
  if (boarding) {
    if (boarding.status === 'boarded') {
      childBanner = { text: `✅ Your child boarded at ${fmtTime(boarding.boarded_at)}`, bg: '#dcfce7', fg: '#166534' };
    } else if (boarding.status === 'absent') {
      childBanner = { text: '❌ Marked absent today', bg: '#fee2e2', fg: '#991b1b' };
    } else if (boarding.status === 'alighted') {
      childBanner = { text: `🏠 Your child got off at ${fmtTime(boarding.alighted_at)}`, bg: '#dbeafe', fg: '#1e40af' };
    }
  }

  // Show most recent 20 events, newest first
  const timeline = [...events].reverse().slice(0, 20);

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {/* Live status hero */}
      <div style={{ background: '#fff', borderRadius: 14, border: `2px solid ${heroColor}`, padding: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, color: heroColor }}>{heroStatus}</div>
            {heroSub && <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>{heroSub}</div>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 8, height: 8, borderRadius: 999, background: active_trip && active_trip.status === 'in_progress' ? '#22c55e' : '#94a3b8', boxShadow: active_trip && active_trip.status === 'in_progress' ? '0 0 0 4px rgba(34,197,94,0.2)' : 'none' }} />
            <span style={{ fontSize: 11, color: '#94a3b8' }}>
              {active_trip && active_trip.status === 'in_progress' ? 'LIVE' : 'Not live'}
              {lastFetch && ` · updated ${fmtTime(lastFetch)}`}
            </span>
          </div>
        </div>
      </div>

      {/* Child status banner */}
      {childBanner && (
        <div style={{ padding: '12px 14px', borderRadius: 10, background: childBanner.bg, color: childBanner.fg, fontWeight: 700, fontSize: 15 }}>
          {childBanner.text}
        </div>
      )}

      {/* Route + bus info card */}
      {route && (
        <div style={{ background: '#fff', padding: 14, borderRadius: 12, border: '1px solid #e2e8f0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: 12, color: '#64748b' }}>Route</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>{route.route_name || '–'}</div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#64748b' }}>Bus</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>{route.bus_number || '–'}{route.registration_no ? ` (${route.registration_no})` : ''}</div>
            </div>
            {route.bus_driver_name && (
              <div>
                <div style={{ fontSize: 12, color: '#64748b' }}>Driver</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>{route.bus_driver_name}</div>
                {route.bus_driver_phone && (
                  <a href={`tel:${route.bus_driver_phone}`} style={{ fontSize: 13, color: '#0ea5e9', textDecoration: 'none' }}>📞 {route.bus_driver_phone}</a>
                )}
              </div>
            )}
          </div>
          {child_stop && (
            <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px dashed #e2e8f0', fontSize: 13, color: '#475569' }}>
              Your child boards at <strong style={{ color: '#0f172a' }}>{child_stop.stop_name}</strong>
              {child_stop.pickup_time ? ` · pickup ~${child_stop.pickup_time}` : ''}
              {child_stop.drop_time ? ` · drop ~${child_stop.drop_time}` : ''}
            </div>
          )}
        </div>
      )}

      {/* Today's trips summary */}
      {trips.length > 0 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {trips.map(t => (
            <div key={t.trip_id} style={{
              padding: '6px 12px', borderRadius: 999, fontSize: 12, fontWeight: 700,
              background: t.status === 'in_progress' ? '#dcfce7' : t.status === 'completed' ? '#e0f2fe' : '#f1f5f9',
              color:      t.status === 'in_progress' ? '#166534' : t.status === 'completed' ? '#075985' : '#64748b',
            }}>
              {t.trip_type === 'morning' ? '🌅 Morning' : '🌆 Evening'} · {t.status.replace('_', ' ')}
              {t.started_at ? ` · ${fmtTime(t.started_at)}` : ''}
            </div>
          ))}
        </div>
      )}

      {/* Timeline */}
      <div style={{ background: '#fff', padding: 14, borderRadius: 12, border: '1px solid #e2e8f0' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 10 }}>📍 Live Timeline</div>
        {timeline.length === 0 ? (
          <div style={{ fontSize: 13, color: '#64748b', padding: '8px 0' }}>
            {active_trip ? 'Waiting for driver updates…' : 'The driver has not started today\'s trip yet.'}
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 8 }}>
            {timeline.map((e) => {
              const cfg = EVENT_LABELS[e.event_type] || { icon: '•', text: () => e.event_type };
              return (
                <div key={e.event_id} style={{
                  display: 'flex', gap: 10, padding: '8px 10px', borderRadius: 8,
                  background: e.is_my_child ? '#f0fdf4' : '#f8fafc',
                  border: e.is_my_child ? '1px solid #bbf7d0' : '1px solid #e2e8f0',
                }}>
                  <div style={{ fontSize: 18 }}>{cfg.icon}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, color: '#0f172a', fontWeight: e.is_my_child ? 700 : 500 }}>
                      {cfg.text(e.stop_name)}
                    </div>
                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{fmtTime(e.event_time)}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div style={{ textAlign: 'center', fontSize: 11, color: '#94a3b8' }}>
        Auto-refreshes every 10 seconds
      </div>
    </div>
  );
}

// ── Tab-based parent student dashboard ───────────────────────────────────────
function ParentStudentDashboard({ schoolCode, studentId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedExam, setExpandedExam] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const year = params.get('year') || '';
    axios.get(`${API}/progress-card/${encodeURIComponent(schoolCode)}/${studentId}${year ? `?year=${year}` : ''}`)
      .then((res) => {
        if (res.data.success) setData(res.data.data);
        else setError(res.data.message || 'Failed to load dashboard');
      })
      .catch((err) => setError(err.response?.data?.message || 'Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, [schoolCode, studentId]);

  const attendancePct = useMemo(() => {
    if (!data?.attendance?.total_days) return 0;
    return Number((((data.attendance.present_days + (data.attendance.half_days || 0) * 0.5) / data.attendance.total_days) * 100).toFixed(1));
  }, [data]);

  if (loading) return (
    <div style={ds.loadingPage}>
      <style>{`@keyframes pd-spin { to { transform: rotate(360deg); } }`}</style>
      <div style={ds.spinner} />
      <p style={{ marginTop: 14, color: '#64748b' }}>Loading dashboard...</p>
    </div>
  );
  if (error) return <div style={ds.loadingPage}><div style={ds.errorBox}>{error}</div></div>;
  if (!data) return null;

  const {
    student, school_name, school_logo_url, academic_year,
    principal_name = '', class_teacher_name = '',
    attendance = {}, homework = [], exams = [], examSubjects = {}, results = {},
    feeBreakdown = [], payments = [],
  } = data;

  const totalPending = feeBreakdown.reduce((s, r) => s + (parseFloat(r.pending) || 0), 0);

  const TABS = [
    { id: 'overview',   label: 'Overview',   icon: '🏠' },
    { id: 'marks',      label: 'Marks',      icon: '📊' },
    { id: 'attendance', label: 'Attendance', icon: '📅' },
    { id: 'homework',   label: 'Homework',   icon: '📝' },
    { id: 'fees',       label: 'Fees',       icon: '💰' },
    { id: 'bus',        label: 'Bus',        icon: '🚌' },
    { id: 'messages',   label: 'Messages',   icon: '💬' },
  ];

  // ── Overview ────────────────────────────────────────────────────────────────
  function renderOverview() {
    const recentExams = [...exams]
      .sort((a, b) => String(b.exam_date || '').localeCompare(String(a.exam_date || '')))
      .slice(0, 2);
    return (
      <div>
        <div style={ds.summaryGrid}>
          <SummaryCard label="Attendance" value={`${attendancePct}%`} tone={attendancePct >= 75 ? 'green' : attendancePct >= 60 ? 'amber' : 'rose'} />
          <SummaryCard label="Exams" value={exams.length} tone="blue" />
          <SummaryCard label="Homework" value={homework.length} tone="green" />
          <SummaryCard label="Fee Pending" value={fmtCurrency(totalPending)} tone={totalPending > 0 ? 'rose' : 'green'} />
        </div>

        {attendance.total_days > 0 && (
          <div style={ds.card}>
            <div style={ds.cardTitle}>Attendance</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
              <AttendanceRing pct={attendancePct} />
              <div style={{ display: 'grid', gap: 8 }}>
                <div style={ds.attStat}><span style={{ ...ds.dot, background: '#16a34a' }} />Present <strong>{attendance.present_days || 0}</strong></div>
                <div style={ds.attStat}><span style={{ ...ds.dot, background: '#dc2626' }} />Absent <strong>{attendance.absent_days || 0}</strong></div>
                <div style={ds.attStat}><span style={{ ...ds.dot, background: '#f59e0b' }} />Half Day <strong>{attendance.half_days || 0}</strong></div>
                <div style={ds.attStat}><span style={{ ...ds.dot, background: '#94a3b8' }} />Total <strong>{attendance.total_days || 0}</strong></div>
              </div>
            </div>
          </div>
        )}

        {recentExams.length > 0 && (
          <div style={ds.card}>
            <div style={ds.cardTitle}>Recent Results</div>
            {recentExams.map((exam) => {
              const subs = examSubjects[exam.exam_id] || [];
              const examRes = results[exam.exam_id] || {};
              const got = subs.reduce((s, sub) => s + (parseFloat(examRes[sub.subject_name]?.marks_obtained || 0) || 0), 0);
              const max = subs.reduce((s, sub) => s + (parseFloat(sub.max_marks || 0) || 0), 0);
              return (
                <div key={exam.exam_id} style={ds.resultRow}>
                  <div>
                    <div style={{ fontWeight: 700, color: '#0f172a' }}>{exam.exam_name}</div>
                    <div style={{ fontSize: 12, color: '#64748b', textTransform: 'capitalize' }}>{exam.exam_type}{exam.exam_date ? ` · ${exam.exam_date}` : ''}</div>
                  </div>
                  <div style={{ fontWeight: 800, color: '#1d4ed8', fontSize: 18 }}>{got}/{max || '–'}</div>
                </div>
              );
            })}
            <button onClick={() => setActiveTab('marks')} style={ds.viewAllBtn}>View All Marks →</button>
          </div>
        )}
      </div>
    );
  }

  // ── Marks ───────────────────────────────────────────────────────────────────
  function renderMarks() {
    if (!exams.length) return <div style={ds.empty}>No exam results yet.</div>;
    const sorted = [...exams].sort((a, b) => String(b.exam_date || '').localeCompare(String(a.exam_date || '')));
    return (
      <div>
        {sorted.map((exam) => {
          const subs = examSubjects[exam.exam_id] || [];
          const examRes = results[exam.exam_id] || {};
          const got = subs.reduce((s, sub) => s + (parseFloat(examRes[sub.subject_name]?.marks_obtained || 0) || 0), 0);
          const max = subs.reduce((s, sub) => s + (parseFloat(sub.max_marks || 0) || 0), 0);
          const pct = max ? Number(((got / max) * 100).toFixed(0)) : null;
          const overallGrade = pct === null ? null : pct >= 90 ? 'A+' : pct >= 80 ? 'A' : pct >= 70 ? 'B+' : pct >= 60 ? 'B' : pct >= 50 ? 'C' : pct >= 35 ? 'D' : 'F';
          const isOpen = expandedExam === exam.exam_id;
          return (
            <div key={exam.exam_id} style={ds.card}>
              <button onClick={() => setExpandedExam(isOpen ? null : exam.exam_id)} style={ds.examHeader}>
                <div>
                  <div style={{ fontWeight: 800, color: '#0f172a', fontSize: 16 }}>{exam.exam_name}</div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 2, textTransform: 'capitalize' }}>
                    {exam.exam_type}{exam.exam_date ? ` · ${exam.exam_date}` : ''}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  {max > 0 && (
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontWeight: 800, color: '#1d4ed8', fontSize: 20 }}>{got}/{max}</div>
                      {pct !== null && <div style={{ fontSize: 12, color: '#64748b' }}>{pct}%</div>}
                    </div>
                  )}
                  <span style={{ color: '#94a3b8', fontSize: 14 }}>{isOpen ? '▲' : '▼'}</span>
                </div>
              </button>

              {isOpen && subs.length > 0 && (
                <div className="table-container">
                <table style={ds.table}>
                  <thead>
                    <tr>
                      <th style={ds.th}>Subject</th>
                      <th style={{ ...ds.th, textAlign: 'right' }}>Marks</th>
                      <th style={{ ...ds.th, textAlign: 'right' }}>Max</th>
                      <th style={{ ...ds.th, textAlign: 'right' }}>Grade</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subs.map((sub) => {
                      const mark = examRes[sub.subject_name];
                      return (
                        <tr key={sub.subject_name} style={ds.tr}>
                          <td style={ds.td}>{sub.subject_name}</td>
                          <td style={{ ...ds.td, textAlign: 'right', fontWeight: 700 }}>{mark ? mark.marks_obtained : '–'}</td>
                          <td style={{ ...ds.td, textAlign: 'right', color: '#94a3b8' }}>{sub.max_marks}</td>
                          <td style={{ ...ds.td, textAlign: 'right' }}><GradeBadge grade={mark?.grade} /></td>
                        </tr>
                      );
                    })}
                    {max > 0 && (
                      <tr style={{ background: '#f0f9ff' }}>
                        <td style={{ ...ds.td, fontWeight: 800 }}>Total</td>
                        <td style={{ ...ds.td, textAlign: 'right', fontWeight: 800, color: '#1d4ed8' }}>{got}</td>
                        <td style={{ ...ds.td, textAlign: 'right', fontWeight: 800 }}>{max}</td>
                        <td style={{ ...ds.td, textAlign: 'right' }}><GradeBadge grade={overallGrade} /></td>
                      </tr>
                    )}
                  </tbody>
                </table>
                </div>
              )}
              {isOpen && !subs.length && <div style={{ ...ds.empty, paddingTop: 8 }}>No subject data recorded.</div>}
            </div>
          );
        })}
      </div>
    );
  }

  // ── Attendance ──────────────────────────────────────────────────────────────
  function renderAttendance() {
    if (!attendance.total_days) return <div style={ds.empty}>No attendance recorded yet.</div>;
    const { present_days = 0, absent_days = 0, half_days = 0, total_days = 0 } = attendance;
    return (
      <div style={ds.card}>
        <div style={ds.cardTitle}>Attendance — {academic_year}</div>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
          <AttendanceRing pct={attendancePct} />
        </div>
        <div style={ds.attGrid}>
          <div style={{ ...ds.attBox, borderColor: '#bbf7d0', background: '#f0fdf4' }}>
            <div style={{ fontSize: 32, fontWeight: 800, color: '#15803d' }}>{present_days}</div>
            <div style={{ fontSize: 13, color: '#166534' }}>Present</div>
          </div>
          <div style={{ ...ds.attBox, borderColor: '#fecaca', background: '#fff1f2' }}>
            <div style={{ fontSize: 32, fontWeight: 800, color: '#dc2626' }}>{absent_days}</div>
            <div style={{ fontSize: 13, color: '#991b1b' }}>Absent</div>
          </div>
          <div style={{ ...ds.attBox, borderColor: '#fde68a', background: '#fffbeb' }}>
            <div style={{ fontSize: 32, fontWeight: 800, color: '#d97706' }}>{half_days}</div>
            <div style={{ fontSize: 13, color: '#92400e' }}>Half Day</div>
          </div>
          <div style={{ ...ds.attBox, borderColor: '#e2e8f0', background: '#f8fafc' }}>
            <div style={{ fontSize: 32, fontWeight: 800, color: '#0f172a' }}>{total_days}</div>
            <div style={{ fontSize: 13, color: '#475569' }}>Total Days</div>
          </div>
        </div>
        {attendancePct < 75 && (
          <div style={ds.alertBox}>
            ⚠️ Attendance is below 75%. Please ensure regular attendance.
          </div>
        )}
      </div>
    );
  }

  // ── Homework ────────────────────────────────────────────────────────────────
  function renderHomework() {
    if (!homework.length) return <div style={ds.empty}>No homework published yet.</div>;
    const today = new Date().toISOString().slice(0, 10);
    return (
      <div>
        {homework.map((item) => {
          const overdue = item.due_date && item.due_date < today;
          return (
            <div key={item.assignment_id} style={{ ...ds.card, borderLeft: `4px solid ${overdue ? '#dc2626' : '#1d4ed8'}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 6 }}>
                <span style={{ background: '#dbeafe', color: '#1d4ed8', borderRadius: 6, padding: '2px 10px', fontSize: 12, fontWeight: 700 }}>
                  {item.subject}
                </span>
                {item.due_date && (
                  <span style={{ fontSize: 12, color: overdue ? '#dc2626' : '#64748b', fontWeight: overdue ? 700 : 400 }}>
                    {overdue ? '⚠️ Overdue: ' : 'Due: '}{item.due_date}
                  </span>
                )}
              </div>
              <div style={{ fontWeight: 800, color: '#0f172a', marginBottom: item.description ? 6 : 0 }}>{item.title}</div>
              {item.description && <div style={{ color: '#475569', fontSize: 14, lineHeight: 1.55 }}>{item.description}</div>}
            </div>
          );
        })}
      </div>
    );
  }

  // ── Fees ────────────────────────────────────────────────────────────────────
  function renderFees() {
    return (
      <div>
        <div style={ds.card}>
          <div style={ds.cardTitle}>Fee Breakdown</div>
          <div className="table-container">
          <table style={ds.table}>
            <thead>
              <tr>
                <th style={ds.th}>Type</th>
                <th style={{ ...ds.th, textAlign: 'right' }}>Charged</th>
                <th style={{ ...ds.th, textAlign: 'right' }}>Paid</th>
                <th style={{ ...ds.th, textAlign: 'right' }}>Pending</th>
              </tr>
            </thead>
            <tbody>
              {feeBreakdown.map((row, i) => (
                <tr key={i} style={ds.tr}>
                  <td style={ds.td}>{row.type}</td>
                  <td style={{ ...ds.td, textAlign: 'right' }}>{fmtCurrency(row.charged)}</td>
                  <td style={{ ...ds.td, textAlign: 'right', color: '#15803d' }}>{fmtCurrency(row.paid)}</td>
                  <td style={{ ...ds.td, textAlign: 'right', color: row.pending > 0 ? '#dc2626' : '#15803d', fontWeight: 700 }}>
                    {fmtCurrency(row.pending)}
                  </td>
                </tr>
              ))}
              <tr style={{ background: '#f0f9ff' }}>
                <td style={{ ...ds.td, fontWeight: 800 }}>Total</td>
                <td style={{ ...ds.td, textAlign: 'right', fontWeight: 800 }}>
                  {fmtCurrency(feeBreakdown.reduce((s, r) => s + (parseFloat(r.charged) || 0), 0))}
                </td>
                <td style={{ ...ds.td, textAlign: 'right', fontWeight: 800, color: '#15803d' }}>
                  {fmtCurrency(feeBreakdown.reduce((s, r) => s + (parseFloat(r.paid) || 0), 0))}
                </td>
                <td style={{ ...ds.td, textAlign: 'right', fontWeight: 800, color: totalPending > 0 ? '#dc2626' : '#15803d' }}>
                  {fmtCurrency(totalPending)}
                </td>
              </tr>
            </tbody>
          </table>
          </div>
        </div>

        {payments.length > 0 && (
          <div style={ds.card}>
            <div style={ds.cardTitle}>Payment History</div>
            {payments.map((p) => (
              <div key={p.payment_id} style={ds.payRow}>
                <div>
                  <div style={{ fontWeight: 600, color: '#0f172a', textTransform: 'capitalize' }}>
                    {String(p.payment_type).replace(/_/g, ' ')}
                    {p.month_year ? ` · ${p.month_year}` : ''}
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>
                    {p.payment_date}{p.receipt_no ? ` · Receipt #${p.receipt_no}` : ''}
                  </div>
                </div>
                <div style={{ fontWeight: 800, color: '#15803d' }}>{fmtCurrency(p.amount)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── Messages ───────────────────────────────────────────────────────────────
  function renderMessages() {
    return <ParentMessagesTab schoolCode={schoolCode} studentId={studentId} />;
  }

  // ── Live Bus tracking ─────────────────────────────────────────────────────
  function renderBus() {
    return <ParentBusTab schoolCode={schoolCode} studentId={studentId} />;
  }

  const tabContent = { overview: renderOverview, marks: renderMarks, attendance: renderAttendance, homework: renderHomework, fees: renderFees, bus: renderBus, messages: renderMessages };

  return (
    <div style={ds.page}>
      {/* Hero header */}
      <div style={ds.hero}>
        <div style={{ display: 'flex', gap: 14, alignItems: 'center', flex: 1, flexWrap: 'wrap' }}>
          <Logo src={school_logo_url} />
          <div>
            <div style={ds.badge}>Parent Dashboard</div>
            <div style={{ color: '#fff', fontWeight: 800, fontSize: 20, marginTop: 4 }}>{school_name}</div>
            <div style={{ color: 'rgba(255,255,255,0.72)', fontSize: 13 }}>{academic_year}</div>
          </div>
        </div>
        <a href={`/parent/${encodeURIComponent(schoolCode)}`} style={ds.switchLink}>Switch ↗</a>
      </div>

      {/* Student info strip */}
      <div style={ds.studentBanner}>
        <div style={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>{student.student_name}</div>
        <div style={{ color: '#475569', marginTop: 4, fontSize: 14 }}>
          Class {student.class}
          {student.father_name ? ` · Father: ${student.father_name}` : ''}
          {student.admission_no ? ` · Adm: ${student.admission_no}` : ''}
        </div>
        {(class_teacher_name || principal_name) && (
          <div style={{ color: '#64748b', marginTop: 6, fontSize: 13, display: 'flex', flexWrap: 'wrap', gap: '0 18px' }}>
            {class_teacher_name && (
              <span>Class Teacher: <strong style={{ color: '#0f172a' }}>{class_teacher_name}</strong></span>
            )}
            {principal_name && (
              <span>Principal: <strong style={{ color: '#0f172a' }}>{principal_name}</strong></span>
            )}
          </div>
        )}
      </div>

      {/* Tab bar */}
      <div style={ds.tabBar}>
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{ ...ds.tabBtn, ...(activeTab === tab.id ? ds.tabBtnActive : {}) }}
          >
            <span style={{ fontSize: 18 }}>{tab.icon}</span>
            <span style={{ fontSize: 11, marginTop: 2 }}>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={ds.content}>
        {(tabContent[activeTab] || tabContent.overview)()}
      </div>

      {/* Spacer so last card isn't hidden behind tab bar on mobile */}
      <div style={{ height: 80 }} />
    </div>
  );
}

export default function ParentDashboard() {
  const { schoolCode, studentId } = parseParentRoute();
  if (!studentId) return <ParentLookup schoolCode={schoolCode} />;
  return <ParentStudentDashboard schoolCode={schoolCode} studentId={studentId} />;
}

const styles = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(180deg, #dbeafe 0%, #f8fafc 36%, #f8fafc 100%)',
    fontFamily: "'Segoe UI', sans-serif",
  },
  lookupShell: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  lookupCard: {
    width: '100%',
    maxWidth: 520,
    background: '#fff',
    borderRadius: 24,
    boxShadow: '0 20px 60px rgba(15, 23, 42, 0.12)',
    padding: 28,
  },
  lookupBadge: {
    display: 'inline-block',
    padding: '6px 12px',
    borderRadius: 999,
    background: '#dbeafe',
    color: '#1d4ed8',
    fontWeight: 700,
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
  },
  lookupTitle: {
    margin: '14px 0 8px',
    color: '#0f172a',
    fontSize: 32,
    lineHeight: 1.1,
  },
  lookupText: {
    margin: 0,
    color: '#475569',
    lineHeight: 1.6,
  },
  lookupSubtle: {
    color: '#64748b',
    fontSize: 13,
    marginBottom: 4,
  },
  lookupInput: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '16px 18px',
    borderRadius: 16,
    border: '2px solid #cbd5e1',
    fontSize: 18,
    fontWeight: 600,
    marginBottom: 12,
  },
  lookupButton: {
    width: '100%',
    padding: '15px 18px',
    border: 'none',
    borderRadius: 16,
    background: '#0f172a',
    color: '#fff',
    fontSize: 16,
    fontWeight: 800,
    cursor: 'pointer',
  },
  installPanel: {
    marginBottom: 18,
    borderRadius: 16,
    background: '#f8fafc',
    border: '1px solid #dbeafe',
    padding: 14,
  },
  installButton: {
    marginTop: 10,
    padding: '10px 14px',
    border: 'none',
    borderRadius: 12,
    background: '#1d4ed8',
    color: '#fff',
    fontWeight: 800,
    cursor: 'pointer',
  },
  errorBox: {
    marginTop: 14,
    padding: '12px 14px',
    borderRadius: 12,
    background: '#fff1f2',
    color: '#be123c',
    fontWeight: 600,
  },
  sectionMiniTitle: {
    fontWeight: 800,
    color: '#0f172a',
    marginBottom: 10,
  },
  studentPick: {
    display: 'block',
    textDecoration: 'none',
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: 14,
    padding: 14,
    marginBottom: 8,
  },
  dashboardShell: {
    maxWidth: 980,
    margin: '0 auto',
    padding: 18,
  },
  hero: {
    background: 'linear-gradient(135deg, #0f172a 0%, #1d4ed8 100%)',
    borderRadius: 28,
    padding: 24,
    display: 'flex',
    justifyContent: 'space-between',
    gap: 14,
    alignItems: 'center',
    flexWrap: 'wrap',
    boxShadow: '0 18px 48px rgba(30, 64, 175, 0.22)',
  },
  switchLink: {
    color: '#fff',
    textDecoration: 'none',
    fontWeight: 700,
    background: 'rgba(255,255,255,0.16)',
    padding: '10px 14px',
    borderRadius: 12,
  },
  studentBanner: {
    background: '#fff',
    borderRadius: 24,
    padding: 24,
    marginTop: 16,
    boxShadow: '0 10px 30px rgba(15, 23, 42, 0.06)',
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 14,
    marginTop: 16,
  },
  summaryCard: {
    borderRadius: 20,
    padding: 18,
    border: '1px solid rgba(148, 163, 184, 0.2)',
  },
  sectionCard: {
    background: '#fff',
    borderRadius: 24,
    padding: 20,
    marginTop: 16,
    boxShadow: '0 10px 30px rgba(15, 23, 42, 0.06)',
  },
  sectionHeader: {
    fontSize: 20,
    fontWeight: 800,
    color: '#0f172a',
    marginBottom: 14,
  },
  twoColStats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: 12,
    color: '#475569',
  },
  emptyText: {
    color: '#64748b',
  },
  homeworkCard: {
    border: '1px solid #e2e8f0',
    borderRadius: 16,
    padding: 14,
    background: '#f8fafc',
  },
  examCard: {
    border: '1px solid #dbeafe',
    borderRadius: 16,
    padding: 14,
    background: '#f8fbff',
  },
  subjectRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 10,
    color: '#334155',
    fontSize: 14,
  },
  loadingPage: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
    background: '#f8fafc',
  },
};

// ── Styles for tab-based dashboard ───────────────────────────────────────────
const ds = {
  page: {
    minHeight: '100vh',
    background: '#f1f5f9',
    fontFamily: "'Segoe UI', sans-serif",
    paddingBottom: 20,
  },
  hero: {
    background: 'linear-gradient(135deg, #0f172a 0%, #1d4ed8 100%)',
    padding: '20px 20px 24px',
    display: 'flex',
    justifyContent: 'space-between',
    gap: 12,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  badge: {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: 999,
    background: 'rgba(255,255,255,0.18)',
    color: '#fff',
    fontWeight: 700,
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
  },
  switchLink: {
    color: '#fff',
    textDecoration: 'none',
    fontWeight: 700,
    background: 'rgba(255,255,255,0.16)',
    padding: '8px 14px',
    borderRadius: 10,
    fontSize: 13,
    whiteSpace: 'nowrap',
  },
  studentBanner: {
    background: '#fff',
    padding: '16px 20px',
    borderBottom: '1px solid #e2e8f0',
  },
  tabBar: {
    display: 'flex',
    background: '#fff',
    borderBottom: '2px solid #e2e8f0',
    overflowX: 'auto',
    position: 'sticky',
    top: 0,
    zIndex: 100,
  },
  tabBtn: {
    flex: '1 0 auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '10px 6px',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    color: '#64748b',
    fontWeight: 600,
    borderBottom: '3px solid transparent',
    gap: 2,
    minWidth: 60,
  },
  tabBtnActive: {
    color: '#1d4ed8',
    borderBottom: '3px solid #1d4ed8',
    background: '#f0f7ff',
  },
  content: {
    maxWidth: 780,
    margin: '0 auto',
    padding: '16px 14px 0',
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: 12,
    marginBottom: 14,
  },
  card: {
    background: '#fff',
    borderRadius: 20,
    padding: '18px 18px',
    marginBottom: 14,
    boxShadow: '0 2px 12px rgba(15,23,42,0.07)',
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: 800,
    color: '#0f172a',
    marginBottom: 14,
  },
  resultRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    padding: '10px 0',
    borderBottom: '1px solid #f1f5f9',
  },
  viewAllBtn: {
    marginTop: 12,
    background: 'none',
    border: 'none',
    color: '#1d4ed8',
    fontWeight: 700,
    cursor: 'pointer',
    padding: 0,
    fontSize: 14,
  },
  attStat: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    color: '#475569',
    fontSize: 14,
  },
  dot: {
    display: 'inline-block',
    width: 10,
    height: 10,
    borderRadius: '50%',
    flexShrink: 0,
  },
  attGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: 12,
    marginBottom: 16,
  },
  attBox: {
    borderRadius: 16,
    border: '2px solid',
    padding: '14px 16px',
    textAlign: 'center',
  },
  alertBox: {
    background: '#fff7ed',
    border: '1px solid #fed7aa',
    color: '#c2410c',
    borderRadius: 12,
    padding: '10px 14px',
    fontSize: 14,
    fontWeight: 600,
  },
  examHeader: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    textAlign: 'left',
    gap: 12,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: 14,
    fontSize: 14,
  },
  th: {
    padding: '8px 10px',
    background: '#f8fafc',
    color: '#64748b',
    fontWeight: 700,
    fontSize: 12,
    textAlign: 'left',
    borderBottom: '2px solid #e2e8f0',
  },
  tr: {
    borderBottom: '1px solid #f1f5f9',
  },
  td: {
    padding: '9px 10px',
    color: '#334155',
    verticalAlign: 'middle',
  },
  payRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    padding: '10px 0',
    borderBottom: '1px solid #f1f5f9',
  },
  empty: {
    color: '#94a3b8',
    textAlign: 'center',
    padding: '28px 0',
    fontSize: 15,
  },
  loadingPage: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
    background: '#f8fafc',
  },
  spinner: {
    width: 40,
    height: 40,
    border: '4px solid #e2e8f0',
    borderTop: '4px solid #1d4ed8',
    borderRadius: '50%',
    animation: 'pd-spin 1s linear infinite',
  },
  errorBox: {
    background: '#fff1f2',
    color: '#be123c',
    borderRadius: 12,
    padding: '14px 18px',
    fontWeight: 600,
  },
};
